import { CheckCircleRounded, DoNotDisturbRounded, NavigateNextRounded } from "@mui/icons-material";
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip, IconButton, Box, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Employee } from "../../types/Employee";
import { ReceiptRequest, ReceiptRequestType } from "../../types/ReceiptRequest";
import { RequestStatus } from "../../types/Type";
import { queryReceiptRequestsByIdNumber } from "../../utils/queryReceiptRequests";
import { updateReceiptRequest } from "../../utils/updateReceiptRequest";
import { ChipIcon, ChipColor } from "../ChipUtils";
import { hasPermission } from "../../security/Permission";
import { useAppSelector } from "../../store";

const Noti = (status: RequestStatus) => {
  if (status === 'New')
    return 'New request has been added!';
  else if (status === 'Processing')
    return 'Request is now being processed!';
  else if (status === 'Approved')
    return 'Request has been approved!'
  else if (status === 'Denied')
    return 'Request has been denied!';
  else
    return 'Request has been cancelled!';
};

export const EmployeeReceiptRequests = (props: { employee: Employee, type: ReceiptRequestType }) => {
  const { employee, type } = props;
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const [receiptRequests, setReceiptRequests] = useState<ReceiptRequest[]>([]);

  const userHasPermission = (
    (type === 'Iqama' && hasPermission(authState, [], ['update.iqamaRenewalReceipts'])) ||
    (type === 'Iqama' && hasPermission(authState, [], ['update.iqamaRenewalReceipts'])) ||
    (type === 'Iqama' && hasPermission(authState, [], ['update.iqamaRenewalReceipts']))
  );

  const approveDenyRequest = (receiptRequest: ReceiptRequest, status: RequestStatus) => {
    updateReceiptRequest(receiptRequest, status).then(() => {
      receiptRequest.status = status;
      const copyRequests = [...receiptRequests];
      const cIdx = copyRequests.findIndex((e) => e.id === receiptRequest.id);
      copyRequests[cIdx] = receiptRequest;
      enqueueSnackbar(Noti(status), { variant: 'success' });
      setReceiptRequests(copyRequests);
      setTimeout(() => {
        copyRequests.splice(cIdx, 1);
        setReceiptRequests([...copyRequests]);
      }, 1000);
    });
  };

  const handleApproveOnClick = (event: MouseEvent<HTMLButtonElement>, receiptRequest: ReceiptRequest) => {
    event.stopPropagation();
    approveDenyRequest(receiptRequest, 'Approved');
  };

  const handleDenyOnClick = (event: MouseEvent<HTMLButtonElement>, receiptRequest: ReceiptRequest) => {
    event.stopPropagation();
    approveDenyRequest(receiptRequest, 'Denied');
  };

  useEffect(() => {
    queryReceiptRequestsByIdNumber(employee.idNumber, type).then((receiptRequests) => {
      setReceiptRequests(receiptRequests);
    });
  }, []);

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={76} align="right">Count</TableCell>
              <TableCell width={120}>Id Number</TableCell>
              <TableCell>Name</TableCell>
              <TableCell width={220}>Date Requested</TableCell>
              <TableCell width={150}>Status</TableCell>
              {userHasPermission && (
                <TableCell width={112}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {receiptRequests.length === 0 && (
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 } 
                }}>
                <TableCell colSpan={6}><Typography variant="caption">No receipt requests to show...</Typography></TableCell>
              </TableRow>
            )}
            {receiptRequests.map((r, i) => {
              return (
                <TableRow 
                  hover={userHasPermission}
                  key={r.id} 
                  // onClick={() => handleRowOnClick(r)}
                  sx={{ cursor: userHasPermission ? 'pointer' : 'default', '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="right">{i + 1}</TableCell>
                  <TableCell>{r.idNumber}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{DateTime.fromISO(r.dateRequested).toLocaleString(DateTime.DATETIME_MED)}</TableCell>
                  <TableCell>
                    <Chip icon={ChipIcon(r.status)} label={r.status} color={ChipColor(r.status)} variant="outlined" />
                  </TableCell>
                  {userHasPermission && (
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Tooltip title="Approve Request">
                          <span>
                            <IconButton color="success" onClick={(e) => handleApproveOnClick(e, r)} disabled={r.status !== 'New' && r.status !== 'Processing'}>
                              <CheckCircleRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Deny Request">
                          <span>
                            <IconButton color="error" onClick={(e) => handleDenyOnClick(e, r)} disabled={r.status !== 'New' && r.status !== 'Processing'}>
                              <DoNotDisturbRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
};